import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth'
import { useStore } from '../store/useStore'
import { api } from '../services/api'
import { auth } from '../lib/firebase'
import { isWeb3Mode } from '../lib/authMode'
import { showToast } from '../components/Toast'
import { useI18n } from '../i18n/useI18n'
import { apiErrorI18nKey, apiErrorRef } from '../lib/apiErrorMessage'

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect'

type Phase =
  | { kind: 'resolving' }
  | { kind: 'error'; error: unknown }

export default function Pay() {
  const { storeId } = useParams()
  const navigate = useNavigate()
  const { t } = useI18n()
  const isLoggedIn = useStore((s) => s.isLoggedIn)
  const stores = useStore((s) => s.stores)
  const setCurrentStore = useStore((s) => s.setCurrentStore)
  const restoreSession = useStore((s) => s.restoreSession)
  const login = useStore((s) => s.login)

  const [web3Mode] = useState(isWeb3Mode)
  const [authResolved, setAuthResolved] = useState(web3Mode)
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [phase, setPhase] = useState<Phase>({ kind: 'resolving' })
  const restoreStartedRef = useRef(false)

  useEffect(() => {
    if (web3Mode) return
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user)
      setAuthResolved(true)
    })
    return unsubscribe
  }, [web3Mode])

  useEffect(() => {
    if (!authResolved || isLoggedIn || restoreStartedRef.current) return

    const hasLocalSession = localStorage.getItem('isLoggedIn') === 'true'
    if (hasLocalSession) {
      restoreStartedRef.current = true
      restoreSession().catch((err) => {
        setPhase({ kind: 'error', error: err })
      })
      return
    }
    if (firebaseUser) {
      restoreStartedRef.current = true
      login({
        name: firebaseUser.displayName || 'User',
        email: firebaseUser.email || '',
      }).catch((err) => {
        setPhase({ kind: 'error', error: err })
      })
    }
    // No local session and no Firebase user → user must log in. The
    // /login redirect is triggered during render (see below) rather than
    // via setState here, since calling setState synchronously in an
    // effect is flagged by react-hooks/set-state-in-effect.
  }, [authResolved, isLoggedIn, firebaseUser, restoreSession, login])

  useEffect(() => {
    if (!isLoggedIn || !storeId) return

    let cancelled = false

    const resolveAndGo = async () => {
      let store = stores.find((s) => s.id === storeId) || null
      if (!store) {
        try {
          const fetched = await api.getStore(storeId)
          if (fetched) store = fetched as typeof store
        } catch (err) {
          if (cancelled) return
          setPhase({ kind: 'error', error: err })
          return
        }
      }
      if (cancelled) return
      if (!store) {
        showToast({ type: 'error', message: t('scan.storeNotFound') })
        navigate('/home', { replace: true })
        return
      }
      setCurrentStore(store)
      navigate('/scan', { replace: true, state: { preScannedStoreId: store.id } })
    }

    resolveAndGo()

    return () => {
      cancelled = true
    }
  }, [isLoggedIn, storeId, stores, setCurrentStore, navigate, t])

  if (!storeId) {
    return <Navigate to="/home" replace />
  }

  const needsLogin =
    authResolved &&
    !isLoggedIn &&
    !firebaseUser &&
    localStorage.getItem('isLoggedIn') !== 'true'

  if (needsLogin) {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, `/pay/${storeId}`)
    return <Navigate to="/login" replace />
  }

  return (
    <div className="min-h-screen bg-warm-white safe-top safe-bottom flex flex-col items-center justify-center px-6">
      {phase.kind === 'error' ? (
        <div className="flex flex-col items-center gap-4 text-center">
          {/* ここはまだ支払い前 (ログイン復元 / 店舗情報の取得で失敗)。以前は
            * scan.paymentFailed (「決済システムに障害」) を出していたが、通信断でも
            * 決済の障害に見えるので、起きたことをそのまま出す */}
          <p className="font-display text-lg font-semibold text-text-primary">
            {t(apiErrorI18nKey(phase.error))}
          </p>
          <p className="font-mono text-xs text-gray-400 break-all max-w-sm">{apiErrorRef(phase.error)}</p>
          <button
            onClick={() => navigate('/home', { replace: true })}
            className="h-12 px-6 rounded-xl bg-navy text-white font-display font-semibold"
          >
            {t('storeReg.back')}
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-yokoso-red animate-spin" />
          <p className="font-body text-sm text-text-secondary">{t('splash.welcome')}</p>
        </div>
      )}
    </div>
  )
}
